import React, { useState, useEffect } from "react";
import {
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Chip,
    LinearProgress,
} from "@material-ui/core";
import { Add, Delete, Check, CheckCircle } from "@material-ui/icons";
import WarningIcon from "@material-ui/icons/Warning";
import { useLinkAssetById, useLinkedAssets } from "./stressEntryQueries";
import { useStressEntryContext } from "./StressEntryContext";
import { useFetchContext } from "../../../common/FetchContext";
import { toast } from "react-toastify";
import { set } from "lodash";

export const SubAssetSelect: React.FC = () => {
    const { state, dispatch } = useStressEntryContext();
    const { authAxios } = useFetchContext();
    const [linkedAssetData, setLinkedAssetData] = useState<any>(null);
    const {
        data: linkedAssets,
        error,
        isLoading,
        isError,
        refetch,
    } = useLinkAssetById(state.selectedStressType?.id, state.mode);

    // ✅ FIX: define local states
    const [assetId, setAssetId] = useState("");
    const [localAssets, setLocalAssets] = useState<any[]>([]);
    const [confirmed, setConfirmed] = useState(false);

    // ✅ Sync with API data
    useEffect(() => {
        if (linkedAssets) {
            setLocalAssets(linkedAssets);
        }
    }, [linkedAssets]);

    const {
        mutateAsync: mutateGetLinkedAssets,
        error: errorlinkedAssets,
        isLoading: isLoadinglinkedAssets,
        isError: iserrorlinkedAssets,
        isSuccess: isSuccesslinkedAssets,
    } = useLinkedAssets();

    if (isLoadinglinkedAssets) {

        return <LinearProgress />;

    }
    const handleAddAsset = async () => {
        try {
            const data = await mutateGetLinkedAssets({
                authAxios,
                id: assetId,
            });

            if (data) {
                // 🚨 1. check validity
                if (!data.is_valid_subasset) {
                    toast.error("This sub-asset is not in calibration. Please select a valid asset.");
                    return;
                }

                // 🚨 2. check duplicate asset_number
                const alreadyExists = localAssets.some(
                    (asset) => asset.asset_number === data.asset_number
                );
                if (alreadyExists) {
                    toast.error("This asset is already linked.");
                    return;
                }

                // ✅ Append new linked asset into local list
                setLocalAssets((prev) => [...prev, data]);
                setLinkedAssetData(data);

                // ✅ Clear input after success
                setAssetId("");
            }
        } catch (err) {

            toast.error("Failed to link asset. Please check the Asset ID and try again.");
        }
    };

    const handleDelete = (subAssetId: string) => {
        setLocalAssets((prev) =>
            prev.filter((asset) => asset.sub_asset_id !== subAssetId)
        );
    };


    const handleReset = async () => {
        dispatch({ type: "CLEAN_LINKED_ASSETS" });
        dispatch({ type: "RESET_SERIAL_NUMBER", payload: { reset: false } });
        setAssetId("");
        setConfirmed(false);
        try {
            const { data } = await refetch();  // ✅ re-fetch linked assets
            if (data) {
                setLocalAssets(data);
            }
        } catch (err) {
            toast.error("Failed to refresh linked assets after reset.");
        }
    };

    const handleConfirm = () => {

        const hasInvalid = localAssets.some(asset => !asset.is_valid_subasset);

        if (hasInvalid) {
            toast.error("One or more sub-assets are not in calibration. Please remove before confirming.");
            return; // stop confirm flow
        }

        setConfirmed(true);
        dispatch({ type: "RESET_SERIAL_NUMBER", payload: { reset: true } });
        dispatch({
            type: "SET_SELECTED_STRESS_LINKED_ASSETS",
            payload: { selectedStressLinkedAssets: localAssets },
        });
    };

    return (
        <Grid container spacing={3} direction="column" alignItems="flex-start">
            {/* Top Controls */}
            <Grid item style={{ width: "100%" }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    {/* Left Side: Asset ID + Link Asset */}
                    <Grid item>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <TextField
                                    label="Asset Number"
                                    value={assetId}
                                    onChange={(e) => setAssetId(e.target.value)}
                                    disabled={confirmed}
                                    InputProps={{
                                        style: confirmed
                                            ? {
                                                backgroundColor: "#e0e0e0",
                                                color: "#757575",
                                                borderRadius: 4,
                                            }
                                            : {},
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<Add />}
                                    style={{
                                        backgroundColor: confirmed ? "#e0e0e0" : "#f9a825",
                                        color: confirmed ? "#757575" : "black",
                                    }}
                                    onClick={handleAddAsset}
                                    disabled={confirmed}
                                >
                                    Link Asset
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Right Side: Reset + Confirm */}
                    <Grid item>
                        <Grid container spacing={2}>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    style={{ backgroundColor: "#8e24aa", color: "white" }}
                                    onClick={handleReset}
                                >
                                    Reset
                                </Button>
                            </Grid>

                            {/* UPDATED: Only show Confirm button if there are assets */}
                            {localAssets && localAssets.length > 0 && (
                                <Grid item>
                                    <Button
                                        variant="contained"
                                        style={{
                                            backgroundColor: confirmed ? "#e0e0e0" : "#43a047",
                                            color: confirmed ? "#757575" : "white",
                                        }}
                                        startIcon={<Check />}
                                        onClick={handleConfirm}
                                        disabled={confirmed}
                                    >
                                        Confirm
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Linked Assets Section */}
            <Grid item xs={12} style={{ width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                    Linked Assets under {state.selectedStressType?.name}
                </Typography>

                <Grid
                    container
                    direction="column"
                    style={{
                        backgroundColor: "#fff",
                        borderRadius: 5,
                        padding: "12px 16px",
                        boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                        minHeight: "100px",
                    }}
                >
                    {/* Empty State Check */}
                    {(!localAssets || localAssets.length === 0) ? (
                        <Grid
                            item
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                                padding: "24px 0"
                            }}
                        >
                            <Typography variant="body1" color="textSecondary">
                                There's no assets linked to this asset.
                            </Typography>
                        </Grid>
                    ) : (
                        /* Asset List */
                        localAssets.map((asset) => (
                            <Grid item key={asset.sub_asset_id}>
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="space-between"
                                    style={{
                                        backgroundColor: "#fff",
                                        borderRadius: 0,
                                        padding: "12px 16px",
                                        borderBottom: "1px solid #f0f0f0"
                                    }}
                                >
                                    {/* Left: SubAsset Info */}
                                    <Grid item>
                                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                                            {asset.sub_asset_name}
                                        </Typography>
                                        <Chip
                                            label={asset.is_valid_subasset ? "in calibration" : "calibration due"}
                                            size="small"
                                            icon={
                                                asset.is_valid_subasset ? (
                                                    <CheckCircle style={{ color: "#388e3c" }} />
                                                ) : (
                                                    <WarningIcon style={{ color: "red" }} />
                                                )
                                            }
                                            style={{
                                                marginTop: 4,
                                                backgroundColor: asset.is_valid_subasset ? "#e8f5e9" : "#ffebee",
                                                color: asset.is_valid_subasset ? "#388e3c" : "red",
                                                fontWeight: 500,
                                                border: asset.is_valid_subasset ? "none" : "1px solid red",
                                            }}
                                        />
                                    </Grid>

                                    {/* Right: Delete Button */}
                                    {!confirmed && (
                                        <Grid item>
                                            <IconButton onClick={() => handleDelete(asset.sub_asset_id)}>
                                                <Delete />
                                            </IconButton>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        ))
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};
